import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Upload, RefreshCw, X, ToggleLeft, ToggleRight, Copy, Image as ImageIcon, GripVertical, Eye, EyeOff, Link as LinkIcon, Calendar, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { 
  adminGetLocations, 
  adminCreateLocation, 
  adminUpdateLocation, 
  adminDeleteLocation,
  uploadImage,
  adminGetLocationPhotos,
  adminUploadLocationPhoto,
  adminBulkUploadLocationPhotos,
  adminUpdateLocationPhoto,
  adminDeleteLocationPhoto,
  adminGetLocationLinks,
  adminCreateLocationLink,
  adminUpdateLocationLink,
  adminDeleteLocationLink,
} from '../../services/api';

const LocationsTab = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [managerLocation, setManagerLocation] = useState(null);
  const locationFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    address: '',
    phone: '',
    reservation_phone: '',
    image: '',
    hours: {
      monday: '11am-10pm',
      tuesday: '11am-10pm',
      wednesday: '11am-10pm',
      thursday: '11am-10pm',
      friday: '11am-12am',
      saturday: '10am-12am',
      sunday: '10am-10pm'
    },
    coordinates: { lat: 33.7547, lng: -84.3733 },
    online_ordering: '',
    reservations: '',
    social_media: { instagram: '', facebook: '', twitter: '' },
    weekly_specials: []
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const data = await adminGetLocations();
      setLocations(data);
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLocationImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Error', description: 'Please upload a valid image (JPG, PNG, GIF, or WebP)', variant: 'destructive' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Image must be less than 10MB', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const result = await uploadImage(file);
      const backendUrl = window.location.origin;
      const fullUrl = `${backendUrl}${result.url}`;
      setFormData({ ...formData, image: fullUrl });
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (locationFileInputRef.current) {
        locationFileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await adminUpdateLocation(editingLocation.id, formData);
        toast({ title: 'Success', description: 'Location updated successfully' });
      } else {
        await adminCreateLocation(formData);
        toast({ title: 'Success', description: 'Location created successfully' });
      }
      setShowForm(false);
      setEditingLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      slug: location.slug || '',
      name: location.name || '',
      address: location.address || '',
      phone: location.phone || '',
      reservation_phone: location.reservation_phone || '',
      image: location.image || '',
      hours: location.hours || {
        monday: 'Closed', tuesday: 'Closed', wednesday: 'Closed',
        thursday: 'Closed', friday: 'Closed', saturday: 'Closed', sunday: 'Closed'
      },
      coordinates: location.coordinates || { lat: 0, lng: 0 },
      online_ordering: location.online_ordering || '',
      reservations: location.reservations || '',
      social_media: location.social_media || { instagram: '', facebook: '', twitter: '' },
      weekly_specials: location.weekly_specials || []
    });
    setShowForm(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await adminDeleteLocation(locationId);
      toast({ title: 'Success', description: 'Location deleted successfully' });
      fetchLocations();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (location) => {
    try {
      await adminUpdateLocation(location.id, { is_active: !location.is_active });
      toast({ title: 'Success', description: `Location ${location.is_active ? 'hidden' : 'shown'}` });
      fetchLocations();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '', name: '', address: '', phone: '', reservation_phone: '', image: '',
      hours: { monday: '11am-10pm', tuesday: '11am-10pm', wednesday: '11am-10pm',
               thursday: '11am-10pm', friday: '11am-12am', saturday: '10am-12am', sunday: '10am-10pm' },
      coordinates: { lat: 33.7547, lng: -84.3733 },
      online_ordering: '', reservations: '',
      social_media: { instagram: '', facebook: '', twitter: '' },
      weekly_specials: []
    });
  };

  const updateHours = (day, value) => {
    setFormData(prev => ({
      ...prev,
      hours: { ...prev.hours, [day]: value }
    }));
  };

  const updateSpecial = (index, field, value) => {
    setFormData(prev => {
      const specials = [...prev.weekly_specials];
      specials[index] = { ...specials[index], [field]: value };
      return { ...prev, weekly_specials: specials };
    });
  };

  const addSpecial = () => {
    setFormData(prev => ({
      ...prev,
      weekly_specials: [...prev.weekly_specials, { day: 'Monday', special: '' }]
    }));
  };

  const removeSpecial = (index) => {
    setFormData(prev => ({
      ...prev,
      weekly_specials: prev.weekly_specials.filter((_, i) => i !== index)
    }));
  };

  const [syncingSpecials, setSyncingSpecials] = useState(false);
  const [syncingHours, setSyncingHours] = useState(false);
  const [resettingHours, setResettingHours] = useState(false);

  const resetHoursToDefault = async () => {
    if (!editingLocation?.id) {
      toast({ title: 'Save the location first', variant: 'destructive' });
      return;
    }
    if (!window.confirm(
      'Reset this location\'s hours to the system default?\n\n' +
      'This will overwrite the current hours and allow future redeploys to keep them in sync automatically.'
    )) return;
    setResettingHours(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(
        `${window.location.origin}/api/admin/locations/${encodeURIComponent(editingLocation.id)}/reset-hours`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.hours) {
        setFormData(prev => ({ ...prev, hours: { ...prev.hours, ...data.hours } }));
      }
      setEditingLocation(prev => prev ? { ...prev, hours_locked: false, hours: data.hours || prev.hours } : prev);
      toast({ title: 'Hours reset', description: 'System default hours restored.' });
      await fetchLocations();
    } catch (err) {
      toast({ title: 'Reset failed', description: err.message, variant: 'destructive' });
    } finally {
      setResettingHours(false);
    }
  };

  const syncHoursToAll = async () => {
    if (!editingLocation?.slug) {
      toast({ title: 'Save the location first', description: 'Sync only works on locations that already exist.', variant: 'destructive' });
      return;
    }
    const label = editingLocation.name || editingLocation.slug;
    if (!window.confirm(
      `This copies the 7-day hours shown here to every OTHER location, ` +
      `overwriting whatever they currently have. Continue?\n\nSource: ${label}`
    )) return;
    setSyncingHours(true);
    try {
      await adminUpdateLocation(editingLocation.id || editingLocation.slug, {
        hours: formData.hours,
      });
      const token = localStorage.getItem('adminToken');
      const res = await fetch(
        `${window.location.origin}/api/admin/locations/${encodeURIComponent(editingLocation.slug)}/sync-hours-to-all`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast({
        title: 'Hours synced',
        description: `${data.days_synced} days copied to ${data.other_locations_updated} other location(s).`,
      });
    } catch (err) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncingHours(false);
    }
  };
  const syncSpecialsToAll = async () => {
    if (!editingLocation?.slug) {
      toast({ title: 'Save the location first', description: 'Sync only works on locations that already exist.', variant: 'destructive' });
      return;
    }
    // Warn before an irreversible overwrite
    const label = editingLocation.name || editingLocation.slug;
    if (!window.confirm(
      `This copies the ${formData.weekly_specials.length} specials shown here to every OTHER location, ` +
      `overwriting whatever they currently have. Continue?\n\nSource: ${label}`
    )) return;

    setSyncingSpecials(true);
    try {
      // Persist the current form's specials first so the source is up to date
      await adminUpdateLocation(editingLocation.id || editingLocation.slug, {
        weekly_specials: formData.weekly_specials,
      });
      const token = localStorage.getItem('adminToken');
      const res = await fetch(
        `${window.location.origin}/api/admin/locations/${encodeURIComponent(editingLocation.slug)}/sync-specials-to-all`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast({
        title: 'Specials synced',
        description: `${data.specials_count} specials copied to ${data.other_locations_updated} other location(s).`,
      });
    } catch (err) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncingSpecials(false);
    }
  };

  const handleToggleFeature = async (location, feature) => {
    try {
      await adminUpdateLocation(location.id, { [feature]: !location[feature] });
      const featureLabels = {
        check_in_enabled: 'Check-In',
        social_wall_enabled: 'Social Wall',
        tip_staff_enabled: 'Tip Staff',
        dj_tips_enabled: 'DJ Tips'
      };
      toast({ title: 'Updated', description: `${featureLabels[feature]} ${location[feature] ? 'disabled' : 'enabled'}` });
      fetchLocations();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="text-white text-center py-8">Loading locations...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Manage Locations ({locations.length})</h2>
        <Button 
          onClick={() => { resetForm(); setEditingLocation(null); setShowForm(true); }}
          className="bg-red-600 hover:bg-red-700"
          data-testid="add-location-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Location
        </Button>
      </div>

      {/* Location Form Modal */}
      {showForm && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingLocation(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Location Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Fin & Feathers - Edgewood"
                    required
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="location-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">URL Slug *</label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="edgewood-atlanta"
                    required
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-300 block mb-1">Address *</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="345 Edgewood Ave SE, Atlanta, GA 30312"
                  required
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Phone *</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(404) 855-5524"
                    required
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Reservation Phone</label>
                  <Input
                    value={formData.reservation_phone}
                    onChange={(e) => setFormData({ ...formData, reservation_phone: e.target.value })}
                    placeholder="(404) 692-1252"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-300 block mb-1">Location Image *</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="Image URL or upload"
                    required
                    className="bg-slate-900 border-slate-700 text-white flex-1"
                  />
                  <input
                    type="file"
                    ref={locationFileInputRef}
                    onChange={handleLocationImageUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => locationFileInputRef.current?.click()}
                    disabled={uploading}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                {formData.image && (
                  <div className="mt-2">
                    <img src={formData.image} alt="Preview" className="w-32 h-20 object-cover rounded border border-slate-600" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Online Ordering URL</label>
                  <Input
                    value={formData.online_ordering}
                    onChange={(e) => setFormData({ ...formData, online_ordering: e.target.value })}
                    placeholder="https://order.toasttab.com/..."
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Reservation Link</label>
                  <Input
                    value={formData.reservations}
                    onChange={(e) => setFormData({ ...formData, reservations: e.target.value })}
                    placeholder="sms:14046921252?..."
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Latitude</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={formData.coordinates.lat}
                    onChange={(e) => setFormData({ ...formData, coordinates: { ...formData.coordinates, lat: parseFloat(e.target.value) } })}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Longitude</label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={formData.coordinates.lng}
                    onChange={(e) => setFormData({ ...formData, coordinates: { ...formData.coordinates, lng: parseFloat(e.target.value) } })}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </div>

              {/* Hours */}
              <div>
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-300">Hours</label>
                    {editingLocation?.hours_locked ? (
                      <span
                        className="text-[10px] uppercase tracking-wide bg-emerald-900/60 text-emerald-300 border border-emerald-700 px-2 py-0.5 rounded"
                        data-testid="hours-locked-badge"
                        title="These hours are customized by admin and will NOT be overwritten on redeploy."
                      >
                        Custom · survives redeploy
                      </span>
                    ) : (
                      <span
                        className="text-[10px] uppercase tracking-wide bg-slate-700 text-slate-300 border border-slate-600 px-2 py-0.5 rounded"
                        data-testid="hours-default-badge"
                        title="System default. Each redeploy resyncs these hours."
                      >
                        System default
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {editingLocation && editingLocation.hours_locked && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={resetHoursToDefault}
                        disabled={resettingHours}
                        className="border-slate-600 text-slate-200 hover:bg-slate-700"
                        data-testid="reset-hours-btn"
                        title="Discard custom hours and restore the system default"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        {resettingHours ? 'Resetting…' : 'Reset to Default'}
                      </Button>
                    )}
                    {editingLocation && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={syncHoursToAll}
                        disabled={syncingHours}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="sync-hours-to-all-btn"
                        title="Copy these hours to every other location"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        {syncingHours ? 'Syncing…' : 'Sync to All Locations'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div key={day}>
                      <label className="text-xs text-slate-400 block capitalize">{day.slice(0, 3)}</label>
                      <Input
                        value={formData.hours[day]}
                        onChange={(e) => updateHours(day, e.target.value)}
                        placeholder="11:00 AM – 12:00 AM"
                        className="bg-slate-900 border-slate-700 text-white text-xs"
                        data-testid={`hours-input-${day}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Save changes to lock these hours so future redeploys preserve your custom times.
                </p>
              </div>

              {/* Weekly Specials */}
              <div>
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                  <label className="text-sm text-slate-300">Weekly Specials</label>
                  <div className="flex gap-2">
                    {editingLocation && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={syncSpecialsToAll}
                        disabled={syncingSpecials}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="sync-specials-to-all-btn"
                        title="Copy these specials to every other location"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        {syncingSpecials ? 'Syncing…' : 'Sync to All Locations'}
                      </Button>
                    )}
                    <Button type="button" size="sm" onClick={addSpecial} className="bg-slate-700">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                {formData.weekly_specials.map((special, index) => (
                  <div key={`special-${special.day || 'new'}-${index}`} className="flex gap-2 mb-2">
                    <select
                      value={special.day}
                      onChange={(e) => updateSpecial(index, 'day', e.target.value)}
                      className="bg-slate-900 border border-slate-700 text-white rounded px-2 py-1 text-sm"
                    >
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <Input
                      value={special.special}
                      onChange={(e) => updateSpecial(index, 'special', e.target.value)}
                      placeholder="$5 Wings & Margaritas"
                      className="bg-slate-900 border-slate-700 text-white flex-1"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeSpecial(index)} className="text-red-400">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  {editingLocation ? 'Update Location' : 'Create Location'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingLocation(null); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Locations List */}
      <div className="grid gap-4">
        {locations.map((location) => (
          <Card key={location.id} className={`border-slate-700 ${location.is_active ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-60'}`}>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-24 h-24 object-cover rounded"
                />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">{location.name}</h3>
                      <p className="text-slate-400 text-sm">{location.address}</p>
                      <p className="text-slate-400 text-sm">{location.phone}</p>
                      <p className="text-slate-500 text-xs mt-1">slug: /{location.slug}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(location)}
                        className={location.is_active ? 'text-green-400' : 'text-slate-500'}
                        title={location.is_active ? 'Hide' : 'Show'}
                      >
                        {location.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setManagerLocation(location)}
                        className="text-purple-400"
                        title="Manage gallery, links & events"
                        data-testid={`gallery-btn-${location.slug}`}
                      >
                        <ImageIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(location)}
                        className="text-blue-400"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(location.id)}
                        className="text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Feature Toggles */}
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <p className="text-xs text-slate-500 mb-2">Features</p>
                    <div className="flex flex-wrap gap-3">
                      {/* Check-In Toggle */}
                      <button
                        onClick={() => handleToggleFeature(location, 'check_in_enabled')}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                          location.check_in_enabled 
                            ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                            : 'bg-slate-700/30 text-slate-500 border border-slate-600/30'
                        }`}
                        data-testid={`toggle-checkin-${location.slug}`}
                      >
                        {location.check_in_enabled ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                        Check-In
                      </button>
                      
                      {/* Social Wall Toggle */}
                      <button
                        onClick={() => handleToggleFeature(location, 'social_wall_enabled')}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                          location.social_wall_enabled 
                            ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                            : 'bg-slate-700/30 text-slate-500 border border-slate-600/30'
                        }`}
                        data-testid={`toggle-socialwall-${location.slug}`}
                      >
                        {location.social_wall_enabled ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                        Social Wall
                      </button>
                      
                      {/* Tip Staff Toggle */}
                      <button
                        onClick={() => handleToggleFeature(location, 'tip_staff_enabled')}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                          location.tip_staff_enabled 
                            ? 'bg-amber-600/20 text-amber-400 border border-amber-600/30' 
                            : 'bg-slate-700/30 text-slate-500 border border-slate-600/30'
                        }`}
                        data-testid={`toggle-tipstaff-${location.slug}`}
                      >
                        {location.tip_staff_enabled ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                        Tip Staff
                      </button>
                      
                      {/* DJ Tips Toggle */}
                      <button
                        onClick={() => handleToggleFeature(location, 'dj_tips_enabled')}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                          location.dj_tips_enabled 
                            ? 'bg-purple-600/20 text-purple-400 border border-purple-600/30' 
                            : 'bg-slate-700/30 text-slate-500 border border-slate-600/30'
                        }`}
                        data-testid={`toggle-djtips-${location.slug}`}
                      >
                        {location.dj_tips_enabled ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                        DJ Tips
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {locations.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          No locations found. Add your first location!
        </div>
      )}

      {/* Location Manager Modal (Gallery / Links / Events) */}
      {managerLocation && (
        <LocationManagerModal
          location={managerLocation}
          onClose={() => setManagerLocation(null)}
        />
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// Location Manager Modal — tabs: Gallery, Links, Events
// -------------------------------------------------------------------
const LocationManagerModal = ({ location, onClose }) => {
  const [activeTab, setActiveTab] = useState('gallery');
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="location-manager-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div>
            <h3 className="text-white font-semibold">Manage — {location.name}</h3>
            <p className="text-xs text-slate-400">Gallery, custom links &amp; events</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-400">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'gallery' ? 'text-white bg-slate-800 border-b-2 border-purple-500' : 'text-slate-400 hover:text-white'}`}
            data-testid="mgr-tab-gallery"
          >
            <ImageIcon className="w-4 h-4" />
            Gallery
          </button>
          <button
            onClick={() => setActiveTab('links')}
            className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'links' ? 'text-white bg-slate-800 border-b-2 border-purple-500' : 'text-slate-400 hover:text-white'}`}
            data-testid="mgr-tab-links"
          >
            <LinkIcon className="w-4 h-4" />
            Custom Links
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'events' ? 'text-white bg-slate-800 border-b-2 border-purple-500' : 'text-slate-400 hover:text-white'}`}
            data-testid="mgr-tab-events"
          >
            <Calendar className="w-4 h-4" />
            Events
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {activeTab === 'gallery' && <GalleryManager slug={location.slug} />}
          {activeTab === 'links' && <CustomLinksManager slug={location.slug} />}
          {activeTab === 'events' && (
            <div className="text-center py-6">
              <p className="text-slate-300 mb-4">
                Events are managed in the main Events tab. Click below to open it filtered to <strong className="text-white">{location.name}</strong>.
              </p>
              <Button
                onClick={() => {
                  onClose();
                  navigate(`/dashboard?tab=events&location=${encodeURIComponent(location.slug)}`);
                }}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="mgr-open-events-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Events Tab
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// GalleryManager — photos CRUD
// -------------------------------------------------------------------
const GalleryManager = ({ slug }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const load = async () => {
    setLoading(true);
    try {
      setPhotos(await adminGetLocationPhotos(slug) || []);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveImg = (url) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${BACKEND}${url}`;
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 20) {
      toast({ title: 'Too many files', description: 'Max 20 photos at a time', variant: 'destructive' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const result = await adminBulkUploadLocationPhotos(slug, files);
      toast({ title: 'Uploaded', description: `${result.created}/${result.total} photos added` });
      await load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      await adminDeleteLocationPhoto(slug, photoId);
      setPhotos(photos.filter((p) => p.id !== photoId));
      toast({ title: 'Deleted', description: 'Photo removed' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (photo) => {
    try {
      const updated = await adminUpdateLocationPhoto(slug, photo.id, { is_active: !photo.is_active });
      setPhotos(photos.map((p) => p.id === photo.id ? { ...p, ...updated } : p));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleCaptionChange = async (photo, caption) => {
    try {
      const updated = await adminUpdateLocationPhoto(slug, photo.id, { caption });
      setPhotos(photos.map((p) => p.id === photo.id ? { ...p, ...updated } : p));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-400">{photos.length} photo{photos.length !== 1 ? 's' : ''}</p>
        <input type="file" ref={fileInputRef} onChange={handleFilesSelected} accept="image/*" multiple className="hidden" />
        <Button
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="gallery-upload-btn"
        >
          {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {uploading ? 'Uploading...' : 'Upload Photos'}
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-slate-400">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading photos...
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-8 text-slate-400">No photos yet. Click "Upload Photos" to add some.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((photo) => (
            <div key={photo.id} className={`rounded-lg overflow-hidden border ${photo.is_active ? 'border-slate-700' : 'border-red-800/50 opacity-60'}`} data-testid={`photo-card-${photo.id}`}>
              <div className="relative aspect-square bg-slate-800">
                <img src={resolveImg(photo.image_url)} alt={photo.caption || 'photo'} className="w-full h-full object-cover" loading="lazy" />
                {!photo.is_active && (
                  <div className="absolute top-1 right-1 text-[10px] bg-red-700 text-white px-1.5 py-0.5 rounded">HIDDEN</div>
                )}
              </div>
              <div className="p-2 bg-slate-800/60">
                <Input
                  placeholder="Caption (optional)"
                  defaultValue={photo.caption || ''}
                  onBlur={(e) => {
                    if (e.target.value !== (photo.caption || '')) handleCaptionChange(photo, e.target.value);
                  }}
                  className="bg-slate-900 border-slate-700 text-white text-xs h-8 mb-2"
                />
                <div className="flex items-center justify-between">
                  <Button
                    size="sm" variant="ghost" onClick={() => handleToggleActive(photo)}
                    className={photo.is_active ? 'text-green-400 text-xs' : 'text-slate-500 text-xs'}
                  >
                    {photo.is_active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                    {photo.is_active ? 'Visible' : 'Hidden'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(photo.id)} className="text-red-400 text-xs">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// CustomLinksManager — add/edit/delete pill-button links per location
// -------------------------------------------------------------------
const COLOR_OPTIONS = ['slate', 'red', 'amber', 'green', 'blue', 'purple', 'indigo', 'pink'];
const COLOR_BG = {
  slate: 'bg-slate-600', red: 'bg-red-600', amber: 'bg-amber-600', green: 'bg-green-600',
  blue: 'bg-blue-600', purple: 'bg-purple-600', indigo: 'bg-indigo-600', pink: 'bg-pink-600',
};

const CustomLinksManager = ({ slug }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ label: '', url: '', color: 'slate' });

  const load = async () => {
    setLoading(true);
    try {
      setLinks(await adminGetLocationLinks(slug) || []);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!draft.label.trim() || !draft.url.trim()) {
      toast({ title: 'Missing field', description: 'Label and URL are required', variant: 'destructive' });
      return;
    }
    setAdding(true);
    try {
      const created = await adminCreateLocationLink(slug, { ...draft });
      setLinks([...links, created]);
      setDraft({ label: '', url: '', color: 'slate' });
      toast({ title: 'Added', description: `"${created.label}" link created` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await adminDeleteLocationLink(slug, id);
      setLinks(links.filter((l) => l.id !== id));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleToggleActive = async (link) => {
    try {
      const updated = await adminUpdateLocationLink(slug, link.id, { is_active: !link.is_active });
      setLinks(links.map((l) => l.id === link.id ? { ...l, ...updated } : l));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleFieldUpdate = async (link, field, value) => {
    if (value === (link[field] || '')) return;
    try {
      const updated = await adminUpdateLocationLink(slug, link.id, { [field]: value });
      setLinks(links.map((l) => l.id === link.id ? { ...l, ...updated } : l));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div>
      {/* Add new link form */}
      <div className="bg-slate-800/60 rounded-lg p-3 mb-4 border border-slate-700">
        <p className="text-sm text-slate-300 mb-2 font-medium">Add new link</p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          <Input
            placeholder="Label (e.g. Catering)"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className="bg-slate-900 border-slate-700 text-white sm:col-span-2"
            data-testid="link-draft-label"
          />
          <Input
            placeholder="https://..."
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            className="bg-slate-900 border-slate-700 text-white sm:col-span-2"
            data-testid="link-draft-url"
          />
          <select
            value={draft.color}
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded px-2 text-white text-sm h-10"
          >
            {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={adding}
          className="mt-2 bg-purple-600 hover:bg-purple-700"
          data-testid="link-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          {adding ? 'Adding...' : 'Add Link'}
        </Button>
      </div>

      {/* Existing links */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading...
        </div>
      ) : links.length === 0 ? (
        <div className="text-center py-6 text-slate-400">No custom links yet. Add one above.</div>
      ) : (
        <div className="space-y-2">
          {links.map((link) => (
            <div key={link.id} className={`flex items-center gap-2 p-2 bg-slate-800/60 rounded border border-slate-700 ${!link.is_active ? 'opacity-60' : ''}`} data-testid={`link-row-${link.id}`}>
              <div className={`w-3 h-10 rounded ${COLOR_BG[link.color] || COLOR_BG.slate}`} />
              <Input
                defaultValue={link.label}
                onBlur={(e) => handleFieldUpdate(link, 'label', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white h-9 w-32"
              />
              <Input
                defaultValue={link.url}
                onBlur={(e) => handleFieldUpdate(link, 'url', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white h-9 flex-1"
              />
              <select
                defaultValue={link.color}
                onChange={(e) => handleFieldUpdate(link, 'color', e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded px-2 text-white text-xs h-9"
              >
                {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Button size="sm" variant="ghost" onClick={() => handleToggleActive(link)} className={link.is_active ? 'text-green-400' : 'text-slate-500'} title={link.is_active ? 'Hide' : 'Show'}>
                {link.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(link.id)} className="text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationsTab;
